import { useState, useContext } from "react";
import "./App.css";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Battery, Clock, Zap, Phone, Mail, MapPin, FileText, CheckCircle } from "lucide-react";
import AgreementForm from "./components/AgreementFormNew";
import { PriceContext } from "./PriceContext";

function App() {
  const [rentalType, setRentalType] = useState("rent");
  const [showAgreement, setShowAgreement] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const prices = useContext(PriceContext);
  const rent1 = prices.find(p => p.product === "Rent1");
  const rent2 = prices.find(p => p.product === "Rent2");
  const rent3 = prices.find(p => p.product === "Rent3");
  
  //console.log("Prices from context:", prices);
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const openLightbox = (imageSrc, imageAlt) => {
    setLightboxImage({ src: imageSrc, alt: imageAlt });
    setZoomLevel(1);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setZoomLevel(1);
  };
  
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };
  
  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
  };
  
  const resetZoom = () => {
    setZoomLevel(1);
  };

  const handleRentNow = (option) => {
    setSelectedOption(option);
    setShowAgreement(true);
    // Scroll to top when agreement form is shown
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };
  
const handleBuyNow = ({ option }) => {
  const item = prices.find(p => p.product === option);
  if (item && item.url) {
    window.location.href = item.url;
  } else {
    console.error("URL not found for option:", option);
  }
};

  if (showAgreement) {
    return <AgreementForm selectedOption={selectedOption} onBack={() => setShowAgreement(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4 animate-in fade-in"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-[#FF8C42] transition z-10 bg-black/50 rounded-full p-2"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Zoom Controls */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
            <button 
              onClick={(e) => { e.stopPropagation(); zoomIn(); }}
              className="bg-black/50 text-white hover:bg-[#FF8C42] transition rounded-full p-3"
              title="Zoom In"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
              </svg>
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); zoomOut(); }}
              className="bg-black/50 text-white hover:bg-[#FF8C42] transition rounded-full p-3"
              title="Zoom Out"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); resetZoom(); }}
              className="bg-black/50 text-white hover:bg-[#FF8C42] transition rounded-full p-3"
              title="Reset Zoom"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <div className="bg-black/70 text-white text-sm px-3 py-1 rounded-full text-center">
              {Math.round(zoomLevel * 100)}%
            </div>
          </div>
          
          {/* Image Container with Zoom */}
          <div className="overflow-auto max-w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.src} 
              alt={lightboxImage.alt}
              className="object-contain rounded-lg shadow-2xl transition-transform duration-300"
              style={{ 
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                cursor: zoomLevel > 1 ? 'move' : 'default'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Header - Sticky Navigation */}
      <header className="bg-[#0f1e2e] text-white py-4 px-6 fixed w-full top-0 z-50 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="https://customer-assets.emergentagent.com/job_coding-journey-17/artifacts/la3tvptm_logo.jpg" alt="Sonic E-bike Logo" className="h-12 w-12 rounded-full" />
            <h1 className="text-2xl font-bold">Sonic E-bikes</h1>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-6">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-[#FF8C42] transition font-semibold">Home</button>
            <button onClick={() => { scrollToSection('bikes'); setRentalType('rent'); }} className="hover:text-[#FF8C42] transition font-semibold">Rent E-Bike</button>
            <button onClick={() => { scrollToSection('bikes'); setRentalType('buy'); }} className="hover:text-[#FF8C42] transition font-semibold">Purchase E-Bike</button>
            <button onClick={() => scrollToSection('features')} className="hover:text-[#FF8C42] transition font-semibold">Features</button>
            <button onClick={() => scrollToSection('gallery')} className="hover:text-[#FF8C42] transition font-semibold">Gallery</button>
            <button onClick={() => scrollToSection('contact')} className="hover:text-[#FF8C42] transition font-semibold">Contact Us</button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-[#FF8C42]/30 animate-in slide-in-from-top">
            <nav className="flex flex-col gap-3 pt-4">
              <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); }} className="text-left py-2 px-4 hover:bg-[#FF8C42]/20 rounded transition font-semibold">🏠 Home</button>
              <button onClick={() => { scrollToSection('bikes'); setRentalType('rent'); setMobileMenuOpen(false); }} className="text-left py-2 px-4 hover:bg-[#FF8C42]/20 rounded transition font-semibold">🚴 Rent E-Bike</button>
              <button onClick={() => { scrollToSection('bikes'); setRentalType('buy'); setMobileMenuOpen(false); }} className="text-left py-2 px-4 hover:bg-[#FF8C42]/20 rounded transition font-semibold">💰 Purchase E-Bike</button>
              <button onClick={() => scrollToSection('features')} className="text-left py-2 px-4 hover:bg-[#FF8C42]/20 rounded transition font-semibold">⚡ Features</button>
              <button onClick={() => scrollToSection('gallery')} className="text-left py-2 px-4 hover:bg-[#FF8C42]/20 rounded transition font-semibold">📸 Gallery</button>
              <button onClick={() => scrollToSection('contact')} className="text-left py-2 px-4 hover:bg-[#FF8C42]/20 rounded transition font-semibold">📞 Contact Us</button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 bg-[#0f1e2e] text-white relative overflow-hidden">
        {/* Animated Background Images */}
        <div className="absolute inset-0 z-0">
          <div className="hero-bg-slider">
            <div className="hero-bg-slide" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/hj3bh5rr_7a62bb59-a37b-4191-b1b4-ac4280e70af8.jpg)'}}></div>
            <div className="hero-bg-slide" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/4bijm9hx_611273FD-6603-4BDC-B1D1-C2A2BB45CAD4.png)'}}></div>
            <div className="hero-bg-slide" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/74u63l0u_562483636_818348237812380_5582341594576516860_n.jpg)'}}></div>
            <div className="hero-bg-slide" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/56sjfgea_e9a25086-61d4-43be-bd02-a4defa5f7fa0.jpg)'}}></div>
            <div className="hero-bg-slide" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/fa9aj6i3_jp.jpg)'}}></div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1e2e]/98 via-[#0f1e2e]/90 to-[#0f1e2e]/75"></div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <div data-testid="hero-content">
            <Badge className="mb-4 bg-[#FF8C42] text-white hover:bg-[#FFA726] text-lg px-4 py-2">🚨 BRAND NEW! MY 2025 VINXS E-BIKE</Badge>
            <h2 className="text-5xl font-bold mb-4">Ready to Ride the Future with Sonic E-bikes?</h2>
            <p className="text-xl mb-6">Perfect for Delivery (Uber Eats, DoorDash, Menulog) or Personal Use! Brand new 2025 design with powerful battery and FREE servicing.</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                <Zap className="text-[#FF8C42]" size={24} />
                <span className="font-semibold">Legal Speed Limit</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                <Clock className="text-[#FF8C42]" size={24} />
                <span className="font-semibold">10 Hour Range</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                <CheckCircle className="text-[#FF8C42]" size={24} />
                <span className="font-semibold">FREE Servicing</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                <CheckCircle className="text-[#FF8C42]" size={24} />
                <span className="font-semibold">Run-Flat Tyres</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                <CheckCircle className="text-[#FF8C42]" size={24} />
                <span className="font-semibold">Fat Tyre</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                <CheckCircle className="text-[#FF8C42]" size={24} />
                <span className="font-semibold">Full Equipment</span>
              </div>
            </div>
            <div className="flex gap-4">
              <Button onClick={() => scrollToSection('bikes')} size="lg" className="bg-[#FF8C42] text-white hover:bg-[#FFA726]" data-testid="view-bikes-btn">
                View Our Bikes
              </Button>
              <Button onClick={() => scrollToSection('contact')} size="lg" variant="outline" className="text-white border-white hover:bg-white hover:text-[#1a2332]" data-testid="contact-btn">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Bike Options Section */}
      <section id="bikes" className="py-16 px-6 bg-[#0f1e2e]">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-white" data-testid="bikes-section-title">Our E-Bikes</h2>
          <p className="text-center text-gray-300 mb-4 text-lg">Choose your battery option and rental or purchase plan</p>
          <p className="text-center text-[#FF8C42] font-semibold mb-8">✨ Perfect for Uber Eats, DoorDash & other delivery jobs</p>
          
          <Tabs value={rentalType} onValueChange={setRentalType} className="w-full max-w-5xl mx-auto" data-testid="rental-type-tabs">
            <TabsList className="grid w-full grid-cols-2 mb-8 h-14" data-testid="rental-tabs-list">
              <TabsTrigger value="rent" className="text-lg data-[state=active]:bg-[#FF8C42] data-[state=active]:text-white" data-testid="rent-tab">💰 Rent</TabsTrigger>
              <TabsTrigger value="buy" className="text-lg data-[state=active]:bg-[#FF8C42] data-[state=active]:text-white" data-testid="buy-tab">🛒 Buy</TabsTrigger>
            </TabsList>

            <TabsContent value="rent" data-testid="rent-content">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Option 1 */}
				<Card className="border-2 border-[#FF8C42] relative hover:shadow-2xl transition" data-testid="option-1-card">
					<Badge className="absolute -top-3 right-4 bg-[#FF8C42] text-white text-base px-4 py-1">⭐ POPULAR</Badge>   
					<CardHeader className="bg-gradient-to-br from-gray-50 to-white">
                    <CardTitle className="text-2xl text-[#1a2332]">🔹 OPTION 1</CardTitle>
                    <CardDescription className="text-base">Perfect for daily deliveries - 8 hours ride time</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex justify-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/m3oyt0eo_rent%20photo.png" 
                        alt="Sonic E-bike Rental"
                        className="w-full max-w-xs rounded-lg shadow-lg object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Battery className="text-green-600" size={28} />
                        <div>
                          <span className="font-bold text-lg block">48V 40Ah Battery</span>
                          <span className="text-sm text-gray-600">Lithium-ion</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Clock className="text-blue-600" size={28} />
                        <span className="font-semibold text-lg">Up to 8 hours ride time</span>
                      </div>
                      <div className="bg-gradient-to-br from-[#FF8C42] to-[#FFA726] p-6 rounded-lg border-2 border-[#FF8C42] shadow-lg shadow-[#FF8C42]/50">
						<p className="text-2xl text-white drop-shadow-lg inline-block" style={{ transform: "rotate(-45deg)", transformOrigin: "bottom left" }}><strong>From</strong></p>
                        <p className="text-5xl font-extrabold text-white text-center drop-shadow-lg" data-testid="option-1-rent-price">{ prices.length > 0 ? `$${prices.find(p => p.product === "Rent1")?.price.toLocaleString()}`: ""}<span className="text-2xl">/week</span></p>
                        <p className="text-sm text-center text-white/90 mt-2 font-semibold">+$200 refundable deposit</p>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/50 mb-3">
                          <p className="text-white text-sm text-center font-semibold">✅ FREE Servicing | ✅ Full Equipment Kit | 🚚 Delivery Available</p>
                        </div>
                        <Button 
                          className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:from-teal-600 hover:to-cyan-700 hover:shadow-lg hover:shadow-teal-500/50 h-14 text-lg transition-all duration-300 transform hover:scale-105" 
                          onClick={() => handleRentNow({ type: 'rent', option: 1, price: rent1?.price, battery: rent1?.battery, weeklyPaymentLink: rent1?.url ? `${rent1.url}?src=embed` : "" })}
                          data-testid="rent-option-1-btn"
                        >
                          <FileText className="mr-2" /> Rent Now - Fill Agreement
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 2 */}
				<Card className="border-2 hover:border-[#FF8C42] transition hover:shadow-xl" data-testid="option-2-card">				
                  <CardHeader className="bg-gradient-to-br from-orange-50 to-white">
                    <CardTitle className="text-2xl text-[#1a2332]">🔹 OPTION 2</CardTitle>
                    <CardDescription className="text-base">Extended range - 10 hours for longer shifts</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex justify-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/m3oyt0eo_rent%20photo.png" 
                        alt="Sonic E-bike Rental"
                        className="w-full max-w-xs rounded-lg shadow-lg object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Battery className="text-green-600" size={28} />
                        <div>
                          <span className="font-bold text-lg block">48V 70Ah Battery</span>
                          <span className="text-sm text-gray-600">Lithium-ion</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Clock className="text-blue-600" size={28} />
                        <span className="font-semibold text-lg">Up to 10 hours ride time</span>
                      </div>
                      <div className="bg-gradient-to-br from-[#FF8C42] to-[#FFA726] p-6 rounded-lg border-2 border-[#FF8C42] shadow-lg shadow-[#FF8C42]/50">
                        <p className="text-2xl text-white drop-shadow-lg inline-block" style={{ transform: "rotate(-45deg)", transformOrigin: "bottom left" }}><strong>From</strong></p>
						<p className="text-5xl font-extrabold text-white text-center drop-shadow-lg" data-testid="option-2-rent-price">{ prices.length > 0 ? `$${prices.find(p => p.product === "Rent2")?.price.toLocaleString()}`: ""}<span className="text-2xl">/week</span></p>
                        <p className="text-sm text-center text-white/90 mt-2 font-semibold">+$200 refundable deposit</p>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/50 mb-3">
                          <p className="text-white text-sm text-center font-semibold">✅ FREE Servicing | ✅ Full Equipment Kit | 🚚 Delivery Available</p>
                        </div>
                        <Button 
                          className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700 hover:shadow-lg hover:shadow-orange-500/50 h-14 text-lg transition-all duration-300 transform hover:scale-105" 
                          onClick={() => handleRentNow({ type: 'rent', option: 2, price: rent2?.price, battery: rent2?.battery, weeklyPaymentLink: rent2?.url ? `${rent2.url}?src=embed` : "" })}
                          data-testid="rent-option-2-btn"
                        >
                          <FileText className="mr-2" /> Rent Now - Fill Agreement
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 3 - NEW */}
                <Card className="border-2 hover:border-[#FF8C42] transition hover:shadow-xl" data-testid="option-3-card">
                  <CardHeader className="bg-gradient-to-br from-gray-50 to-white">
                    <CardTitle className="text-2xl text-[#1a2332]">🔹 OPTION 3</CardTitle>
                    <CardDescription className="text-base">Maximum range - 12 hours for extended shifts</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex justify-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/m3oyt0eo_rent%20photo.png" 
                        alt="Sonic E-bike Rental"
                        className="w-full max-w-xs rounded-lg shadow-lg object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Battery className="text-green-600" size={28} />
                        <div>
                          <span className="font-bold text-lg block">48V 80Ah Battery</span>
                          <span className="text-sm text-gray-600">Lithium-ion</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Clock className="text-blue-600" size={28} />
                        <span className="font-semibold text-lg">Up to 12 hours ride time</span>
                      </div>
                      <div className="bg-gradient-to-br from-[#FF8C42] to-[#FFA726] p-6 rounded-lg border-2 border-[#FF8C42] shadow-lg shadow-[#FF8C42]/50">
                        <p className="text-2xl text-white drop-shadow-lg inline-block" style={{ transform: "rotate(-45deg)", transformOrigin: "bottom left" }}><strong>From</strong></p>
						<p className="text-5xl font-extrabold text-white text-center drop-shadow-lg" data-testid="option-3-rent-price">{ prices.length > 0 ? `$${prices.find(p => p.product === "Rent3")?.price.toLocaleString()}`: ""}<span className="text-2xl">/week</span></p>
                        <p className="text-sm text-center text-white/90 mt-2 font-semibold">+$200 refundable deposit</p>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/50 mb-3">
                          <p className="text-white text-sm text-center font-semibold">✅ FREE Servicing | ✅ Full Equipment Kit | 🚚 Delivery Available</p>
                        </div>
                        <Button 
                          className="w-full bg-gradient-to-r from-violet-600 to-purple-700 text-white hover:from-violet-700 hover:to-purple-800 hover:shadow-lg hover:shadow-violet-600/50 h-14 text-lg transition-all duration-300 transform hover:scale-105" 
                          onClick={() => handleRentNow({ type: 'rent', option: 3, price: rent3?.price, battery: rent3?.battery, weeklyPaymentLink: rent3?.url ? `${rent3.url}?src=embed` : "" })}
                          data-testid="rent-option-3-btn"
                        >
                          <FileText className="mr-2" /> Rent Now - Fill Agreement
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
			  <div className="mt-8 space-y-4"> 
			    <div className="relative z-10 bg-gradient-to-r from-[#DC2626]/60 to-[#EC4899]/60 p-3 rounded-xl border-2 border-[#C35C5C]"> 
				  <p className="text-center text-white mb-3">
					Prices change based on how many weeks you rent the bike. The prices listed above assume a <strong>12‑week</strong> rental. Get in touch with us for a personalised quote.
				  </p>			  
				</div>
			  </div>
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-[#FF8C42]/20 to-[#FFA726]/20 p-6 rounded-xl border-2 border-[#FF8C42]">
                  <p className="text-center text-xl font-bold text-[#FF8C42] mb-2">💡 Rent-to-Own Available!</p>
                  <p className="text-center text-gray-300 mb-4">Own the bike after completing rental payments</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/50">
                      <p className="text-[#FF8C42] font-bold mb-2">48V 40Ah Battery:</p>
					  <ul className="text-gray-300 text-sm space-y-1">
						{prices.filter(p => p.product.startsWith("RentToOwn1-")).sort((a, b) => b.weekCount - a.weekCount).map(item => (
							<li key={item.product}>• {item.weekCount} weeks at ${item.price}/week</li>
						))}
					  </ul>
                    </div>
                    <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/50">
                      <p className="text-[#FF8C42] font-bold mb-2">48V 70Ah Battery:</p>
					  <ul className="text-gray-300 text-sm space-y-1">
						{prices.filter(p => p.product.startsWith("RentToOwn2-")).sort((a, b) => b.weekCount - a.weekCount).map(item => (
							<li key={item.product}>• {item.weekCount} weeks at ${item.price}/week</li>
						))}
					  </ul>
                    </div>
                    <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/50">
                      <p className="text-[#FF8C42] font-bold mb-2">48V 80Ah Battery:</p>
					  <ul className="text-gray-300 text-sm space-y-1">
						{prices.filter(p => p.product.startsWith("RentToOwn3-")).sort((a, b) => b.weekCount - a.weekCount).map(item => (
							<li key={item.product}>• {item.weekCount} weeks at ${item.price}/week</li>
						))}
					  </ul>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">🛠️</p>
                    <p className="text-white font-semibold text-sm">FREE Servicing</p>
                  </div>
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">⚡</p>
                    <p className="text-white font-semibold text-sm">Legal Speed Limit</p>
                  </div>
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">🔋</p>
                    <p className="text-white font-semibold text-sm">10 Hour Range</p>
                  </div>
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">🛞</p>
                    <p className="text-white font-semibold text-sm">Run-Flat Tyres</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="buy" data-testid="buy-content">
              <div className="grid md:grid-cols-3 gap-8">
			  
                {/* Option 1 Buy */}
                <Card className="border-2 hover:border-[#FF8C42] transition hover:shadow-xl" data-testid="buy-option-1-card">
                  <CardHeader className="bg-gradient-to-br from-gray-50 to-white">
                    <CardTitle className="text-2xl text-[#1a2332]">🔹 OPTION 1</CardTitle>
                    <CardDescription className="text-base">Good Starter Package - 8 hours ride time</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex justify-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/njblgxxv_purchase%20photo.png" 
                        alt="Sonic E-bike Purchase"
                        className="w-full max-w-xs rounded-lg shadow-lg object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Battery className="text-green-600" size={28} />
                        <div>
                          <span className="font-bold text-lg block">48V 40Ah Battery</span>
                          <span className="text-sm text-gray-600">Lithium-ion</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Clock className="text-blue-600" size={28} />
                        <span className="font-semibold text-lg">Up to 8 hours ride time</span>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg border-2 border-green-400 shadow-lg shadow-green-500/50">
                        <p className="text-5xl font-extrabold text-white text-center drop-shadow-lg" data-testid="option-1-buy-price"> { prices.length > 0 ? `$${prices.find(p => p.product === "BuyNow1")?.price.toLocaleString()}`: ""}</p>
                        <p className="text-sm text-center text-white/90 mt-2 font-semibold">One-time payment</p>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/50 mb-3">
                          <p className="text-white text-sm text-center font-semibold">✅ Full Equipment Kit | 🚚 Delivery Available</p>
                        </div>
                        <Button 
                          className="w-full bg-green-600 text-white hover:bg-green-700 hover:shadow-lg hover:shadow-green-500/50 h-14 text-lg transition-all duration-300 transform hover:scale-105" 
                          onClick={() => handleBuyNow({ option: 'BuyNow1' })}
                          data-testid="buy-option-1-btn"
                        >
                          <FileText className="mr-2" /> Buy Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 2 Buy */}
                <Card className="border-2 border-[#FF8C42] relative hover:shadow-2xl transition" data-testid="buy-option-2-card">
                  <Badge className="absolute -top-3 right-4 bg-[#FF8C42] text-white text-base px-4 py-1">💎 BEST VALUE</Badge>
                  <CardHeader className="bg-gradient-to-br from-orange-50 to-white">
                    <CardTitle className="text-2xl text-[#1a2332]">🔹 OPTION 2</CardTitle>
                    <CardDescription className="text-base">Premium battery - 10 hours extended range</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex justify-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/njblgxxv_purchase%20photo.png" 
                        alt="Sonic E-bike Purchase"
                        className="w-full max-w-xs rounded-lg shadow-lg object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Battery className="text-green-600" size={28} />
                        <div>
                          <span className="font-bold text-lg block">48V 70Ah Battery</span>
                          <span className="text-sm text-gray-600">Lithium-ion</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Clock className="text-blue-600" size={28} />
                        <span className="font-semibold text-lg">Up to 10 hours ride time</span>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg border-2 border-green-400 shadow-lg shadow-green-500/50">
                        <p className="text-5xl font-extrabold text-white text-center drop-shadow-lg" data-testid="option-2-buy-price"> { prices.length > 0 ? `$${prices.find(p => p.product === "BuyNow2")?.price.toLocaleString()}`: ""}</p>
                        <p className="text-sm text-center text-white/90 mt-2 font-semibold">One-time payment</p>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/50 mb-3">
                          <p className="text-white text-sm text-center font-semibold">✅ Full Equipment Kit | 🚚 Delivery Available</p>
                        </div>
                        <Button 
                          className="w-full bg-green-600 text-white hover:bg-green-700 hover:shadow-lg hover:shadow-green-500/50 h-14 text-lg transition-all duration-300 transform hover:scale-105" 
                          onClick={() => handleBuyNow({ option: 'BuyNow2' })}
                          data-testid="buy-option-2-btn"
                        >
                          <FileText className="mr-2" /> Buy Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 3 Buy - NEW */}
                <Card className="border-2 hover:border-[#FF8C42] transition hover:shadow-xl" data-testid="buy-option-3-card">
                  <CardHeader className="bg-gradient-to-br from-gray-50 to-white">
                    <CardTitle className="text-2xl text-[#1a2332]">🔹 OPTION 3</CardTitle>
                    <CardDescription className="text-base">Maximum capacity - 12 hours ride time</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex justify-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ebikes-signature/artifacts/njblgxxv_purchase%20photo.png" 
                        alt="Sonic E-bike Purchase"
                        className="w-full max-w-xs rounded-lg shadow-lg object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Battery className="text-green-600" size={28} />
                        <div>
                          <span className="font-bold text-lg block">48V 80Ah Battery</span>
                          <span className="text-sm text-gray-600">Lithium-ion</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Clock className="text-blue-600" size={28} />
                        <span className="font-semibold text-lg">Up to 12 hours ride time</span>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg border-2 border-green-400 shadow-lg shadow-green-500/50">
                        <p className="text-5xl font-extrabold text-white text-center drop-shadow-lg" data-testid="option-3-buy-price"> { prices.length > 0 ? `$${prices.find(p => p.product === "BuyNow3")?.price.toLocaleString()}`: ""}</p>
                        <p className="text-sm text-center text-white/90 mt-2 font-semibold">One-time payment</p>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/50 mb-3">
                          <p className="text-white text-sm text-center font-semibold">✅ Full Equipment Kit | 🚚 Delivery Available</p>
                        </div>
                        <Button 
                          className="w-full bg-green-600 text-white hover:bg-green-700 hover:shadow-lg hover:shadow-green-500/50 h-14 text-lg transition-all duration-300 transform hover:scale-105" 
                          onClick={() => handleBuyNow({ option: 'BuyNow3' })}
                          data-testid="buy-option-3-btn"
                        >
                          <FileText className="mr-2" /> Buy Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-[#FF8C42]/20 to-[#FFA726]/20 p-6 rounded-xl border-2 border-[#FF8C42]">
                  <p className="text-center text-xl font-bold text-[#FF8C42] mb-2">💡 Rent-to-Own Available!</p>
                  <p className="text-center text-gray-300 mb-4">Own the bike after completing rental payments</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/50">
                      <p className="text-[#FF8C42] font-bold mb-2">48V 40Ah Battery:</p>
					  <ul className="text-gray-300 text-sm space-y-1">
						{prices.filter(p => p.product.startsWith("RentToOwn1-")).sort((a, b) => b.weekCount - a.weekCount).map(item => (
							<li key={item.product}>• {item.weekCount} weeks at ${item.price}/week</li>
						))}
					  </ul>
                    </div>
                    <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/50">
                      <p className="text-[#FF8C42] font-bold mb-2">48V 70Ah Battery:</p>
					  <ul className="text-gray-300 text-sm space-y-1">
						{prices.filter(p => p.product.startsWith("RentToOwn2-")).sort((a, b) => b.weekCount - a.weekCount).map(item => (
							<li key={item.product}>• {item.weekCount} weeks at ${item.price}/week</li>
						))}
					  </ul>
                    </div>
                    <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/50">
                      <p className="text-[#FF8C42] font-bold mb-2">48V 80Ah Battery:</p>
					  <ul className="text-gray-300 text-sm space-y-1">
						{prices.filter(p => p.product.startsWith("RentToOwn3-")).sort((a, b) => b.weekCount - a.weekCount).map(item => (
							<li key={item.product}>• {item.weekCount} weeks at ${item.price}/week</li>
						))}
					  </ul>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">🎒</p>
                    <p className="text-white font-semibold text-sm">Full Equipment</p>
                  </div>
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">⚡</p>
                    <p className="text-white font-semibold text-sm">Legal Speed Limit</p>
                  </div>
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">🔋</p>
                    <p className="text-white font-semibold text-sm">10 Hour Range</p>
                  </div>
                  <div className="bg-[#0a1628] p-4 rounded-lg border border-[#FF8C42]/30 text-center">
                    <p className="text-[#FF8C42] font-bold text-lg mb-1">🛞</p>
                    <p className="text-white font-semibold text-sm">Fat Run-Flat</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-6 bg-[#0a1628]">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-white" data-testid="features-title">What's Included</h2>
          <p className="text-center text-gray-300 mb-12 text-lg">Everything you need to start earning!</p>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-[#FF8C42]/30 hover:border-[#FF8C42] transition bg-[#0f1e2e]" data-testid="equipment-card">
              <CardHeader className="bg-gradient-to-br from-[#FF8C42]/20 to-[#0f1e2e]">
                <CardTitle className="text-xl text-white">🎒 Full Equipment Kit</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-300">
                <ul className="space-y-3 text-base">
                  <li className="flex items-center gap-2">🪖 Premium Helmet</li>
                  <li className="flex items-center gap-2">🔒 High-Security Lock</li>
                  <li className="flex items-center gap-2">📱 Phone Holder + USB Charging</li>
                  <li className="flex items-center gap-2">🧳 Large Delivery Bag</li>
                  <li className="flex items-center gap-2">🔌 Fast Charger</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#FF8C42]/30 hover:border-[#FF8C42] transition bg-[#0f1e2e]" data-testid="premium-features-card">
              <CardHeader className="bg-gradient-to-br from-[#FF8C42]/20 to-[#0f1e2e]">
                <CardTitle className="text-xl text-white">⚡ Premium Features</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-300">
                <ul className="space-y-3 text-base">
                  <li className="flex items-center gap-2">🛞 Fat Tyres + Run-Flat System</li>
                  <li className="flex items-center gap-2">⚡ Powerful Motor</li>
                  <li className="flex items-center gap-2">🔋 Long-Range Lithium Battery</li>
                  <li className="flex items-center gap-2">🚀 Dual Speed Modes</li>
                  <li className="flex items-center gap-2">💪 Heavy-Duty Durable Frame</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#FF8C42]/30 hover:border-[#FF8C42] transition bg-[#0f1e2e]" data-testid="support-card">
              <CardHeader className="bg-gradient-to-br from-[#FF8C42]/20 to-[#0f1e2e]">
                <CardTitle className="text-xl text-white">🛠️ Support & Service</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-300">
                <ul className="space-y-3 text-base">
                  <li className="flex items-center gap-2">🛠️ <strong>FREE Servicing</strong> (rentals)</li>
                  <li className="flex items-center gap-2">🔧 Low Maintenance Design</li>
                  <li className="flex items-center gap-2">🚚 Delivery Available</li>
                  <li className="flex items-center gap-2">📦 Limited Stock</li>
                  <li className="flex items-center gap-2">💯 Quality Guarantee</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-16 px-6 bg-[#0f1e2e]">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-white" data-testid="gallery-title">Our E-Bikes in Action</h2>
          <p className="text-center text-gray-300 mb-12 text-lg">See the quality and versatility</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div 
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
              onClick={() => openLightbox("https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/onr84ppw_7a62bb59-a37b-4191-b1b4-ac4280e70af8.jpg", "Sonic E-bike with delivery bag")}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/onr84ppw_7a62bb59-a37b-4191-b1b4-ac4280e70af8.jpg" 
                alt="Sonic E-bike with delivery bag" 
                className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <p className="text-white font-bold text-lg">🚚 Delivery Ready</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
            <div 
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
              onClick={() => openLightbox("https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/o17hzg21_533137049_1292176305774986_941825465971777136_n.jpg", "E-bike with 2-seaters")}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/o17hzg21_533137049_1292176305774986_941825465971777136_n.jpg" 
                alt="E-bike with 2-seaters" 
                className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <p className="text-white font-bold text-lg">👥 2-Seaters</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
            <div 
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
              onClick={() => openLightbox("https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/rfdd1xyi_562483636_818348237812380_5582341594576516860_n.jpg", "E-bike delivery rack")}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/rfdd1xyi_562483636_818348237812380_5582341594576516860_n.jpg" 
                alt="E-bike delivery rack" 
                className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <p className="text-white font-bold text-lg">🚚 Delivery Rack</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
            <div 
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
              onClick={() => openLightbox("https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/ea3w1k0m_IMG_2222.jpeg", "E-bike in action")}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/ea3w1k0m_IMG_2222.jpeg" 
                alt="E-bike in action" 
                className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <p className="text-white font-bold text-lg">⚡ Ready to Ride</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
            <div 
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
              onClick={() => openLightbox("https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/3tf50see_IMG_2716.jpg", "E-bike close-up")}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rent-buy-ebikes/artifacts/3tf50see_IMG_2716.jpg" 
                alt="E-bike close-up" 
                className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <p className="text-white font-bold text-lg">🔧 Premium Build</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
            <div 
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
              onClick={() => openLightbox("https://customer-assets.emergentagent.com/job_coding-journey-17/artifacts/j8r88oai_611273FD-6603-4BDC-B1D1-C2A2BB45CAD4.png", "Sonic E-bike with full kit")}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_coding-journey-17/artifacts/j8r88oai_611273FD-6603-4BDC-B1D1-C2A2BB45CAD4.png" 
                alt="Sonic E-bike with full kit" 
                className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <p className="text-white font-bold text-lg">🎒 Full Equipment Kit</p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-6 bg-[#0f1e2e]">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-4 text-white" data-testid="contact-title">Get in Touch</h2>
          <p className="text-center text-gray-300 mb-12 text-lg">We're here to help you get started!</p>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-[#FF8C42] bg-[#0a1628]" data-testid="contact-info-card">
              <CardHeader className="bg-gradient-to-br from-[#FF8C42]/20 to-[#0a1628]">
                <CardTitle className="text-2xl text-white">📍 Visit Us</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="flex items-start gap-4 p-4 bg-[#0f1e2e] rounded-lg border border-[#FF8C42]/30">
                  <MapPin className="text-[#FF8C42] mt-1" size={28} />
                  <div>
                    <p className="font-bold text-lg text-white">Location</p>
                    <p className="text-gray-300">Waterloo, NSW</p>
                    <p className="text-gray-300">Sydney, Australia</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#0f1e2e] rounded-lg border border-[#FF8C42]/30">
                  <Phone className="text-green-500 mt-1" size={28} />
                  <div>
                    <p className="font-bold text-lg text-white">Phone</p>
                    <p className="text-gray-300 text-lg">0420 993 083</p>
                    <p className="text-gray-300 text-lg">0417 232 258</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#0f1e2e] rounded-lg border border-[#FF8C42]/30">
                  <Mail className="text-blue-500 mt-1" size={28} />
                  <div>
                    <p className="font-bold text-lg text-white">Email</p>
                    <p className="text-gray-300">admin@sonicebike.com.au</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#0f1e2e] rounded-lg border border-[#FF8C42]/30 hover:border-[#FF8C42] transition-all duration-300">
                  <svg className="w-7 h-7 text-[#FF8C42] mt-1" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  <div>
                    <p className="font-bold text-lg text-white">Instagram</p>
                    <a href="https://www.instagram.com/sonicebike/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C42] hover:text-[#FFA726] transition-colors duration-300 font-semibold">@sonicebike</a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#FF8C42]/20 to-[#0a1628] border-2 border-[#FF8C42]" data-testid="urgent-message-card">
              <CardHeader>
                <CardTitle className="text-2xl text-white">⚠️ Limited Stock!</CardTitle>
                <CardDescription className="text-base text-gray-300">Message now to reserve your e-bike</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <p className="text-center text-lg font-semibold text-gray-200">Call or email us today to check availability and book your test ride!</p>
                <div className="flex flex-col gap-4">
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-14 text-lg" 
                    onClick={() => window.location.href = 'tel:0420993083'}
                    data-testid="call-now-btn"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call: 0420 993 083
                  </Button>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg" 
                    onClick={() => window.location.href = 'mailto:admin@sonicebike.com.au'}
                    data-testid="email-now-btn"
                  >
                    <Mail className="mr-2 h-5 w-5" />
                    Email Us
                  </Button>
                  <Button 
                    className="w-full bg-[#FF8C42] hover:bg-[#FFA726] text-white h-14 text-lg" 
                    onClick={() => scrollToSection('bikes')}
                    data-testid="view-options-btn"
                  >
                    <FileText className="mr-2 h-5 w-5" />
                    View Rental Options
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f1e2e] text-white py-12 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://customer-assets.emergentagent.com/job_coding-journey-17/artifacts/la3tvptm_logo.jpg" alt="Sonic Logo" className="h-12 w-12 rounded-full" />
                <h3 className="text-2xl font-bold">Sonic E-bikes</h3>
              </div>
              <p className="text-gray-300">Ride the Future. Earn More. Go Further.</p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => scrollToSection('bikes')} className="hover:text-[#FF8C42]">Rental Options</button></li>
                <li><button onClick={() => scrollToSection('features')} className="hover:text-[#FF8C42]">Features</button></li>
                <li><button onClick={() => scrollToSection('gallery')} className="hover:text-[#FF8C42]">Gallery</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-[#FF8C42]">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Business Hours</h4>
              <p className="text-gray-300">Monday - Friday: 9am - 6pm</p>
              <p className="text-gray-300">Saturday: 10am - 4pm</p>
              <p className="text-gray-300">Sunday: By appointment</p>
            </div>
          </div>
          <div className="border-t border-gray-600 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400">© 2025 Sonic E-bikes. ABN: 52 342 103 170. All rights reserved.</p>
              <div className="flex items-center gap-4">
                <a href="https://www.instagram.com/sonicebike/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-gradient-to-r from-[#FF8C42] to-[#FFA726] px-6 py-3 rounded-lg hover:from-[#FFA726] hover:to-[#FF8C42] transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#FF8C42]/50">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  <span className="font-bold">Follow Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
